import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CarouselList = ({ refreshKey }) => {
  const [carouselItems, setCarouselItems] = useState([]);
  const API_BASE_URL = process.env.REACT_APP_BACKEND_API;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    itemId: null,
    itemTitle: ''
  });
  const [viewMode, setViewMode] = useState('desktop'); // 'desktop' or 'mobile'

  useEffect(() => {
    const fetchCarouselItems = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/list-carousal`);
        setCarouselItems(response.data.carousals);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to fetch carousel items');
        setCarouselItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselItems();
  }, [refreshKey]);

  const handleDelete = async () => {
    if (!deleteModal.itemId) return;
    
    try {
      await axios.delete(`${API_BASE_URL}/delete-carousal/${deleteModal.itemId}`);
      
      // Optimistic UI update
      setCarouselItems(prevItems => 
        prevItems.filter(item => item.id !== deleteModal.itemId)
      );
      
      // Close modal
      setDeleteModal({ isOpen: false, itemId: null, itemTitle: '' });
    } catch (err) {
      setError('Failed to delete carousel item');
      setDeleteModal({ isOpen: false, itemId: null, itemTitle: '' });
    }
  };

  const openDeleteModal = (id, title) => {
    setDeleteModal({
      isOpen: true,
      itemId: id,
      itemTitle: title
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      itemId: null,
      itemTitle: ''
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex justify-center">
          <svg className="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-gray-600 text-sm">Loading slides...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-red-600 text-sm">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="mt-2 px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (carouselItems.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-gray-600 text-sm">No slides are currently available. Add a new slide to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* View Mode Toggle */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-1">
          <button
            onClick={() => setViewMode('desktop')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'desktop'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Desktop View
          </button>
          <button
            onClick={() => setViewMode('mobile')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'mobile'
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Mobile View
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Confirm Deletion</h3>
              <button
                onClick={closeDeleteModal}
                className="text-gray-500 hover:text-gray-700"
              >
                &times;
              </button>
            </div>
            <p className="mb-4">Are you sure you want to delete "{deleteModal.itemTitle}"?</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Carousel Items List */}
      {carouselItems.map(item => (
        <div key={item.id} className="border rounded-lg p-4">
          {/* Item Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h4 className="font-medium text-lg">{item.title}</h4>
              <p className="text-gray-600 text-sm">{item.subtitle}</p>
              <p className="text-xs text-gray-400 mt-1">ID: {item.id}</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => openDeleteModal(item.id, item.title)}
                className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-600 rounded"
              >
                Delete
              </button>
            </div>
          </div>

          {/* Images Display */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Desktop Image */}
            <div className={`${viewMode === 'mobile' ? 'opacity-50' : ''}`}>
              <div className="flex items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Desktop (1920x1080)</span>
                {viewMode === 'desktop' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded">
                    Active View
                  </span>
                )}
              </div>
              <img 
                src={item.desktopImage} 
                alt={`${item.title} - Desktop`} 
                className="rounded-lg w-full h-32 object-cover border" 
              />
            </div>

            {/* Mobile Image */}
            <div className={`${viewMode === 'desktop' ? 'opacity-50' : ''}`}>
              <div className="flex items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Mobile (1080x1920)</span>
                {viewMode === 'mobile' && (
                  <span className="ml-2 px-2 py-1 text-xs bg-indigo-100 text-indigo-600 rounded">
                    Active View
                  </span>
                )}
              </div>
              <div className="flex justify-center lg:justify-start">
                <img 
                  src={item.mobileImage} 
                  alt={`${item.title} - Mobile`} 
                  className="rounded-lg w-20 h-32 object-cover border" 
                />
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-xs text-gray-500">
              <span>Created: {new Date(item.createdAt).toLocaleDateString()}</span>
              <span>Updated: {new Date(item.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CarouselList;